import React, { useState } from 'react';

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchInfinite, fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import WebhookIcon from '@dashboard/icons/WebhookIcon';
import Button from '@dashboard/components/Button';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { stringify } from 'query-string';
import WebhooksForm from './WebhooksForm';
import Spinner from '@dashboard/components/Spinner/Spinner';
import Badge from '@dashboard/components/Badge';
import { format } from 'date-fns';
import Tooltip from '@dashboard/components/Tooltip';
import Dialog from '@dashboard/components/Dialog';
import Link from '@dashboard/components/Link';
import GoogleSheetIcon from '@dashboard/icons/GoogleSheetIcon';
import { confirm } from '@dashboard/lib/confirm';
import GoogleSheetsForm from './GoogleSheetsForm';
import Features from '@dashboard/enums/features';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import ZapierIcon from '@dashboard/icons/ZapierIcon';
import {
  integrationEventsKey,
  integrationKey,
  integrationsPageKey,
} from '@dashboard/lib/queryKeys';
import usePage from '@dashboard/hooks/usePage';
import Divider from '@dashboard/components/Divider';
import { upperFirst } from 'lodash';

const allIntegrations = [
  {
    name: 'Google Sheets',
    type: 'googleSheets',
    description: 'Send data to a Google Sheet',
    addTitle: 'Connect to Google Sheets',
    editTitle: 'Edit Google Sheets integration',
    learnMoreLink: '/help/articles/google-sheets',
    icon: GoogleSheetIcon,
    enum: Features.GOOGLE_SHEETS,
  },
  {
    name: 'Webhooks',
    type: 'webhook',
    description: 'Send data to your own server or third-party services',
    addTitle: 'Add a webhook URL',
    editTitle: 'Edit webhook URL',
    learnMoreLink: '/help/articles/webhooks',
    icon: WebhookIcon,
    enum: Features.WEBHOOKS,
  },
  {
    name: 'Zapier',
    type: 'zapier',
    description: 'Connect with thousands of apps',
    learnMoreLink: '/help/articles/integrate-with-zapier',
    icon: ZapierIcon,
    addIntegrationLink: 'https://zapier.com/apps/checkout-page/integrations',
    enum: Features.ZAPIER,
  },
];

export default function IntegrationsForm() {
  const { page } = usePage();
  const { token } = useAuth();
  const { emitToast } = useToast();

  const { data: integrations } = useQuery({
    queryKey: integrationsPageKey(page._id),
    queryFn: async () => fetcher(`/pages/${page._id}/integrations`, token),
    enabled: !!page._id,
  });

  return (
    <div>
      {integrations?.length > 0 && (
        <>
          <div className="mt-4">
            <div className="font-semibold">Active integrations</div>

            <div>
              {integrations?.map(integration => (
                <IntegrationListItem key={integration._id} integration={integration} page={page} />
              ))}
            </div>
          </div>

          <Divider />
        </>
      )}

      <div className="mt-4 mb-4">
        <div className="font-semibold">Available integrations</div>
        <div className="mt-4">
          {allIntegrations?.map(integration => (
            <CanUseFeature key={integration.type} feature={integration.enum}>
              <div key={integration.name} className="py-4 border-b border-gray-100 last:border-b-0">
                <div className="flex items-baseline justify-between gap-x-2">
                  <div className="flex-1">
                    <div className="flex gap-x-2">
                      {integration.icon && <integration.icon className="w-6 h-6" />}
                      <div className="space-y-1 flex-1">
                        <div className="font-semibold">
                          <span>{integration.name}</span>

                          {integration.beta && (
                            <Badge appearance="success" className="ml-2">
                              beta
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm">
                          {integration.description}.{' '}
                          <Link href={integration.learnMoreLink} target="_blank">
                            Learn more
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 flex items-center gap-x-1 ml-auto">
                    {integration.addIntegrationLink ? (
                      <Button
                        appearance="secondary"
                        href={integration.addIntegrationLink}
                        target="_blank"
                      >
                        Add integration
                      </Button>
                    ) : (
                      <Dialog
                        title={`Add ${integration.name} integration`}
                        description={
                          <>
                            <div>
                              {integration.description}.{' '}
                              <Link href={integration.learnMoreLink} target="_blank">
                                Learn more
                              </Link>
                            </div>
                          </>
                        }
                        cancelButton={null}
                        confirmButton={null}
                        trigger={<Button appearance="secondary">Add integration</Button>}
                        size="2xl"
                      >
                        {({ close }) => (
                          <EditIntegration
                            integration={integration}
                            page={page}
                            onDone={() => {
                              emitToast({
                                title: 'Integration added',
                              });
                              close();
                            }}
                          />
                        )}
                      </Dialog>
                    )}
                  </div>
                </div>
              </div>
            </CanUseFeature>
          ))}
        </div>
      </div>
    </div>
  );
}

function getIntegrationLabel(integration, page) {
  switch (integration.type) {
    case 'webhook':
      return integration.config.url;
    case 'googleSheets':
      return (
        <span>
          Sheet connected to {upperFirst(page.type)} page.{' '}
          <Link
            href={`https://docs.google.com/spreadsheets/d/${integration.config.spreadsheetId}`}
            target="_blank"
            variant="secondary"
          >
            Open sheet
          </Link>
        </span>
      );
  }
}

function IntegrationListItem({ integration, page }) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const { emitToast } = useToast();

  const { data: integrationData, isLoading } = useQuery({
    queryKey: integrationKey(integration._id),
    queryFn: async () => {
      return fetcher(`/pages/${integration.pageId}/integrations/${integration._id}`, token);
    },
    enabled: !!integration._id,
  });

  const { mutateAsync: deleteIntegration } = useMutation({
    mutationFn: async () => {
      return fetcher(`/pages/${integration.pageId}/integrations/${integration._id}`, token, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsPageKey(page._id) });
      queryClient.invalidateQueries({ queryKey: integrationKey(integration._id) });
    },
  });

  if (isLoading) {
    return null;
  }

  const integrationInfo = allIntegrations.find(
    integration => integration.type === integrationData.type,
  );

  const name = integrationInfo?.name;
  const label = getIntegrationLabel(integration, page);

  return (
    <div className="py-4 border-b border-gray-100 last:border-b-0">
      <div className="flex items-baseline justify-between gap-x-4">
        <div className="flex-1 ">
          <div className="flex gap-x-2">
            {integrationInfo?.icon && <integrationInfo.icon className="w-6 h-6" />}

            <div className="space-y-1 flex-1">
              <div className="font-semibold">{name}</div>
              <div className="text-sm break-all">{label}</div>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 flex items-center gap-x-2 ml-auto">
          <Tooltip label="Events log" position="bottom">
            <Dialog
              title={`${integrationInfo.name} events log`}
              cancelButton={null}
              confirmButton={null}
              trigger={
                <Button appearance="icon-button">
                  <ClockIcon className="w-4 h-4" />
                </Button>
              }
              size="5xl"
            >
              <EventsLog integration={integration} />
            </Dialog>
          </Tooltip>

          <Tooltip label="Edit" position="bottom">
            <Dialog
              title={integrationInfo.editTitle}
              description={
                <>
                  <div>
                    {integrationInfo.description}.{' '}
                    <Link href={integrationInfo.learnMoreLink} target="_blank">
                      Learn more
                    </Link>
                  </div>
                </>
              }
              cancelButton={null}
              confirmButton={null}
              trigger={
                <Button appearance="icon-button">
                  <PencilIcon className="w-4 h-4" />
                </Button>
              }
              size="2xl"
            >
              {({ close }) => (
                <EditIntegration
                  integration={integration}
                  page={page}
                  onDone={() => {
                    emitToast({
                      title: 'Integration updated',
                    });
                    close();
                  }}
                />
              )}
            </Dialog>
          </Tooltip>

          <Tooltip label="Delete" position="bottom">
            <Button
              appearance="icon-button"
              onClick={async () => {
                const del = await confirm('Delete integration?', {
                  description: 'This will stop sending updates through this integration.',
                });

                if (del) {
                  await deleteIntegration();
                }
              }}
            >
              <TrashIcon className="w-4 h-4" />
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

function EventsLog({ integration }) {
  const { token } = useAuth();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: integrationEventsKey(integration._id),
    queryFn: async ({ pageParam }) => {
      return fetchInfinite(
        `/integrations/${integration._id}/logs?${stringify({
          limit: 10,
          skip: pageParam * 10,
        })}`,
        token,
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
    enabled: !!integration._id,
  });

  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return <div>Unable to show events. Please refresh or contact support.</div>;
  }

  const isEmpty = !data?.pages?.[0]?.data?.length;

  if (isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center gap-y-2">
        <div className="text-gray-500">No events yet</div>
      </div>
    );
  }

  const hasMore = !!data?.pages?.[data?.pages?.length - 1]?.has_more;

  // make a tailwind table layout for events log
  // status, event id, date, retries
  return (
    <table className="border-collapse table-auto w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200">
          <th className="border-r font-normal px-4 py-2 text-gray-400 text-left">Status</th>
          <th className="border-r font-normal px-4 py-2 text-gray-400 text-left">Event ID</th>
          <th className="border-r font-normal px-4 py-2 text-gray-400 text-left">Date</th>
          <th className="font-normal px-4 py-2 text-gray-400 text-left">Retries</th>
          <th className="font-normal px-4 py-2 text-gray-400 text-left"></th>
        </tr>
      </thead>
      <tbody>
        {data?.pages?.map(data => data?.data.map(log => <EventsLogItem log={log} key={log._id} />))}

        {isFetchingNextPage && (
          <tr>
            <td colSpan={4} className="px-4 py-2">
              <Spinner />
            </td>
          </tr>
        )}

        {!isFetchingNextPage && hasMore && (
          <tr>
            <td colSpan={4} className="px-4 py-2">
              <Button onClick={fetchNextPage} appearance="secondary">
                Load more
              </Button>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function getJsonResponse(response) {
  try {
    return JSON.stringify(JSON.parse(response), null, 2);
  } catch (e) {
    return response;
  }
}

function getStatusBadgeAppearane(deliveryStatus) {
  switch (deliveryStatus) {
    case 'succeeded':
      return 'success';
    case 'failed':
      return 'error';
    case 'pending':
    default:
      return 'info';
  }
}

function EventsLogItem({ log }) {
  const { token } = useAuth();
  const [showEvent, setShowEvent] = useState(false);
  const { emitToast } = useToast();
  const queryClient = useQueryClient();

  const response = getJsonResponse(log.response);

  const resendIntegrationMutation = useMutation({
    mutationFn: async () =>
      fetcher(`/integrations/${log.integrationId}/logs/${log._id}/resend`, token, {
        method: 'POST',
      }),
    onSuccess: result => {
      if (result.statusCode === 200) {
        emitToast({
          title: 'Event resent',
        });
      } else {
        emitToast({
          title: 'Resend failed',
          type: 'error',
        });
      }
    },
    onError: () => {
      emitToast({
        title: 'Resend failed',
        type: 'error',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: integrationEventsKey(log.integrationId) });
    },
  });

  return (
    <>
      <tr
        key={log._id}
        className="border-b cursor-pointer"
        onClick={() => setShowEvent(!showEvent)}
      >
        <td className="px-4 py-2 flex gap-x-2">
          {showEvent ? (
            <ChevronDownIcon className="w-4 h-4" />
          ) : (
            <ChevronRightIcon className="w-4 h-4" />
          )}
          <Badge appearance={getStatusBadgeAppearane(log.deliveryStatus)}>
            {log.deliveryStatus}
          </Badge>
        </td>
        <td className="px-4 py-2">{log._id}</td>
        <td className="px-4 py-2">{format(new Date(log.createdAt), 'd MMM, yyyy, HH:mm')}</td>
        <td className="px-4 py-2">{log.retries}</td>
        <td className="px-4 py-2 ml-auto text-right">
          <Button
            size="small"
            appearance="secondary"
            onClick={event => {
              event.stopPropagation();
              resendIntegrationMutation.mutate();
            }}
            disabled={resendIntegrationMutation.isLoading}
          >
            Resend
            {resendIntegrationMutation.isLoading && (
              <Spinner className="ml-2 h-5 w-5 !text-current" />
            )}
          </Button>
        </td>
      </tr>

      {showEvent && (
        <>
          <tr>
            <td colSpan="1" className="px-4 py-2">
              HTTP status code
            </td>
            <td colSpan="3" className="px-4 py-2">
              {log.statusCode}
            </td>
          </tr>
          <tr>
            <td colSpan="1" className="px-4 py-2 align-top">
              Payload
            </td>
            <td colSpan="3" className="px-4 py-2">
              <pre className="text-xs">{log.payload && JSON.stringify(log.payload, null, 2)}</pre>
            </td>
          </tr>
          <tr>
            <td colSpan="1" className="px-4 py-2 align-top">
              Response
            </td>
            <td colSpan="3" className="px-4 py-2">
              <pre className="text-xs">{response}</pre>
            </td>
          </tr>
        </>
      )}
    </>
  );
}

function EditIntegration({ integration, page, onDone }) {
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();

  const createIntegrationMutation = useMutation({
    mutationFn: async ({ values }) =>
      fetcher(`/pages/${page._id}/integrations`, token, {
        method: 'POST',
        body: JSON.stringify({
          ...values,
          type: integration.type,
          sellerId: sellerId,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsPageKey(page._id) });
      queryClient.invalidateQueries({ queryKey: integrationKey(integration._id) });
      onDone();
    },
  });

  const updateIntegrationMutation = useMutation({
    mutationFn: async ({ integrationId, values }) =>
      fetcher(`/pages/${page._id}/integrations/${integrationId}`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKey(integration._id) });
      onDone();
    },
  });

  async function onSubmit(values) {
    try {
      let data;

      if (integration._id) {
        data = await updateIntegrationMutation.mutateAsync({
          integrationId: integration._id,
          values,
        });
      } else {
        data = await createIntegrationMutation.mutateAsync({ values });
      }

      return {
        data,
      };
    } catch (err) {
      return {
        error: err,
      };
    }
  }

  switch (integration.type) {
    case 'webhook':
      return <WebhooksForm integration={integration} onSubmit={onSubmit} />;
    case 'googleSheets':
      return <GoogleSheetsForm integration={integration} onSubmit={onSubmit} />;
    default:
      return null;
  }
}
